/**
 * Отслеживает плагины, для которых был отправлен plugin_request,
 * но ответ ещё не получен.
 *
 * Ответственности:
 *  - Дедупликация: не отправлять plugin_request дважды
 *  - Таймаут: если ответ не пришёл за PLUGIN_REQUEST_TIMEOUT_MS — снимаем ожидание
 *    (позволяет повторно запросить при следующем событии того же типа)
 *
 * Узкое место:
 *   При реконнекте Set сбрасывается через clear(). Метод pendingTypes()
 *   используется в useWsConnection для повторной отправки plugin_request
 *   по всем типам из DeferredEventBuffer — это решает проблему зависших плагинов.
 */
const PLUGIN_REQUEST_TIMEOUT_MS = 15_000

export class PendingPluginRequestTracker {
  private pending = new Set<string>()
  private timers = new Map<string, ReturnType<typeof setTimeout>>()

  /** @returns true если запрос нужно отправить (ещё не в ожидании) */
  request(pluginType: string): boolean {
    if (this.pending.has(pluginType)) return false

    this.pending.add(pluginType)

    // Автоснятие ожидания по таймауту — без удаления буфера
    const timer = setTimeout(() => {
      this.pending.delete(pluginType)
      this.timers.delete(pluginType)
    }, PLUGIN_REQUEST_TIMEOUT_MS)

    this.timers.set(pluginType, timer)
    return true
  }

  /** Вызывается при plugin_ready или plugin_not_available */
  resolve(pluginType: string): void {
    const timer = this.timers.get(pluginType)
    if (timer) clearTimeout(timer)
    this.timers.delete(pluginType)
    this.pending.delete(pluginType)
  }

  isPending(pluginType: string): boolean {
    return this.pending.has(pluginType)
  }

  /** Сброс при реконнекте — все таймеры очищаются */
  clear(): void {
    this.timers.forEach((t) => clearTimeout(t))
    this.timers.clear()
    this.pending.clear()
  }
}

export const pendingPluginRequestTracker = new PendingPluginRequestTracker()
